import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import "./BadgeRequirementTileV2.scss";
import { QUALIFICATION_STATUS } from "../../../enums/incentive-enums";
import LeafTile from "../../../leaf/LeafTile/LeafTile";
import { IconShield, BADGE_TYPE, BADGE_SIZE } from "../../../icons/IconShield/IconShield";
import { AwardProgressBadge } from "../../AwardProgressBadge/AwardProgressBadge";
import { AWARD_PROGRESS } from "../../../enums/award-progress-codes";
import LeafPinLevelName from "../../../leaf/LeafPinLevelName/LeafPinLevelName";
import { useAppSelector } from "../../../../store";
import { getCurrentPeriod, getSelectedPerformanceYearData, getPerformanceYearShort } from "../../../util/period";
import defaultContent from "../../../../content/requirements";

/**
 * BadgeRequirementTileV2 - Consolidated pin level requirement tile
 *
 *
 * USAGE:
 *
 * 1. Manual Mode - Hide description completely:
 *    <BadgeRequirementTileV2
 *      title="Maintain Pin"
 *      description=""  // Empty string = hide description section
 *      status={status}
 *    />
 *
 * 2. Manual Mode - Show custom description:
 *    <BadgeRequirementTileV2
 *      title="Some Requirement"
 *      description="Must qualify Founders Platinum"
 *      status={status}
 *    />
 *
 * 3. Auto Mode - GAR-Aware (checks GAR, PY-based comparison):
 *    <BadgeRequirementTileV2
 *      title="Qualified Founder's Platinum Pin or Above"
 *      // description omitted = auto-generate from pin data
 *      enableGarTracking={true}
 *      status={status}
 *    />
 *
 * 4. Auto Mode - In-Market Only (no GAR, period-based comparison):
 *    <BadgeRequirementTileV2
 *      title="Qualified Silver Producer"
 *      // description omitted = auto-generate from pin data
 *      enableGarTracking={false}  // or omit since default is false
 *      status={status}
 *    />
 */

type Props = {
  title: ReactNode;
  status: string;
  description?: string | React.ReactElement; // empty string to hide description
  enableGarTracking?: boolean;
  // If true: Check GAR level, use PY-based comparison
  // If false: Only use in-market pin, use period-based comparison (default)
  showLabel?: boolean;
  hideBorder?: boolean;
  circleCheckmarkId?: string;
  show?: boolean;
  isLoading?: boolean;
};

export const BadgeRequirementTileV2 = ({
  title,
  description,
  status,
  enableGarTracking = false,
  showLabel = true,
  hideBorder = false,
  circleCheckmarkId = "",
  show = true,
  isLoading = false,
}: Props) => {
  const { shellConfig } = useAppSelector((state) => state.boot);
  const { selectedPeriod } = useAppSelector((state) => state.period);
  const { t } = useTranslation(["requirements"]);

  // Compute badge type and progress status directly from status prop (derived state)
  const badgeType =
    status === QUALIFICATION_STATUS.QUALIFIED
      ? BADGE_TYPE.EARNED
      : status === QUALIFICATION_STATUS.NOT_QUALIFIED || status === QUALIFICATION_STATUS.NOT_TRACKING
        ? BADGE_TYPE.NOT_EARNED
        : BADGE_TYPE.POTENTIAL;

  const progressStatus =
    status === QUALIFICATION_STATUS.QUALIFIED
      ? AWARD_PROGRESS.ACHIEVED
      : status === QUALIFICATION_STATUS.NOT_QUALIFIED || status === QUALIFICATION_STATUS.NOT_TRACKING
        ? AWARD_PROGRESS.NOT_TRACKING
        : AWARD_PROGRESS.TRACKING;

  /**
   * Generate description content based on mode
   *
   * MANUAL MODE: Use description as-is
   *   - description="" → returns "" → description hidden
   *   - description="text" → returns "text" → shows custom text
   *   - description={<JSX/>} → returns JSX → shows custom JSX
   *
   * AUTO MODE: Generate pin level description from shellConfig.aboInfo
   *   - description={undefined} (omitted) → generates pin description JSX
   */
  const getDescription = (): ReactNode => {
    // MANUAL MODE: Use custom description provided by caller
    if (description !== undefined) {
      return description;
    }

    // AUTO MODE: Generate description from pin data
    const {
      garAwardTrackingLevel,
      currentTrackingPin,
      previousPYQualifiedInMarketPin,
      previousGarAwardRank,
      currentGarAwardRank,
    } = shellConfig.aboInfo ?? {};
    const currentPeriod = getCurrentPeriod();

    const { selectedPYStartPeriod, selectedPYEndPeriod } = getSelectedPerformanceYearData(currentPeriod);
    const isCurrentPY =
      Number(selectedPeriod) >= Number(selectedPYStartPeriod) && Number(selectedPeriod) <= Number(selectedPYEndPeriod);
    const isPreviousPY = !isCurrentPY;

    // Determine pin level and label key
    let pinLevel: number | undefined;
    let labelKey: string;

    // Check if GAR tracking is enabled and GAR level exists
    const isGarTracking = enableGarTracking && Boolean(garAwardTrackingLevel && garAwardTrackingLevel > 0);

    if (isGarTracking) {
      // GAR Mode: Use GAR tracking pin
      pinLevel = isPreviousPY ? previousGarAwardRank : (currentGarAwardRank ?? -1);
      labelKey = isPreviousPY ? "pinLevelLabel" : "pinTrackingLevelLabel";
    } else {
      // In-Market Mode: Pin level and label both use PY-based context
      pinLevel = isPreviousPY ? previousPYQualifiedInMarketPin : (currentTrackingPin ?? -1);
      labelKey = isPreviousPY ? "inMarketPinLabel" : "inMarketPinTrackingLabel";
    }

    // Get default translation text
    const defaultText = defaultContent[labelKey as keyof typeof defaultContent] as string;

    // Build translated label text
    const performanceYear = getPerformanceYearShort(selectedPeriod);
    const translatedLabel = t(labelKey, defaultText, isPreviousPY ? { performanceYear } : {}).toString();

    // Return structured description with label and pin level
    return (
      <div>
        <p className="descriptionLabel">{translatedLabel}</p>
        <p className="subDescription">
          <LeafPinLevelName pinLevelCode={pinLevel ?? 0} />
        </p>
      </div>
    );
  };

  const finalDescription = getDescription();

  // Auto-determine if description should be shown
  // Hide if: null, undefined, empty string, or whitespace-only string
  const shouldShowDescription =
    finalDescription != null && (typeof finalDescription !== "string" || finalDescription.trim().length > 0);

  if (show === false) {
    return <></>;
  }

  const content = (
    <div className="BadgeReqV2__container">
      <LeafTile
        title={title}
        showMetStatus={true}
        met={status === QUALIFICATION_STATUS.QUALIFIED}
        hideBorder={hideBorder}
        circleCheckmarkId={circleCheckmarkId}
        isLoading={isLoading}
      >
        <div className="BadgeReqV2">
          <div className="BadgeReqV2__icon">
            <IconShield size={BADGE_SIZE.EXTRA_LARGE} type={badgeType} />
          </div>
          {shouldShowDescription && <div className="BadgeReqV2__desc">{finalDescription}</div>}
          {showLabel ? (
            <AwardProgressBadge status={progressStatus} hideIcon={true} />
          ) : (
            <div className="BadgeReqV2__phantomLabel"></div>
          )}
        </div>
      </LeafTile>
    </div>
  );

  // Apply wrapper class based on mode to match original styling
  if (enableGarTracking === true) {
    // GAR tracking mode - matches GarOrInMarketPinLevelTile styling
    return <div className="BadgeReqV2__wrapper--gar">{content}</div>;
  }

  // In-Market only mode (enableGarTracking === false)
  return <div className="BadgeReqV2__wrapper--inmarket">{content}</div>;
};
